/**
 * Retina-resolution custom CSS cursor via SVG data URLs + -webkit-image-set().
 *
 * Custom violet cursor arrow with white outline (22px) used everywhere —
 * interactive elements, default state, etc. Text inputs fall back to browser
 * auto (I-beam), disabled elements to system not-allowed, and drag elements
 * to system grab/grabbing.
 *
 * Uses -webkit-image-set() with 1x + 2x SVGs for retina sharpness on Chrome/Safari,
 * with a plain url() fallback for Firefox.
 */
import { useEffect } from 'react';

// ── Arrow cursor ─────────────────────────────────────────────────────────────
// Diagonal send/cursor arrow in a 24×24 viewBox.
const ARROW_PATH =
  'M20.5056 10.7754C21.1225 10.5355 21.431 10.4155 21.5176 10.2459C21.5926 10.099 21.5903 9.92446 21.5115 9.77954C21.4205 9.61226 21.109 9.50044 20.486 9.2768L4.59629 3.5728C4.0866 3.38983 3.83175 3.29835 3.66514 3.35605C3.52029 3.40621 3.40645 3.52004 3.35629 3.6649C3.29859 3.8315 3.39008 4.08635 3.57304 4.59605L9.277 20.4858C9.50064 21.1088 9.61246 21.4203 9.77973 21.5113C9.92465 21.5901 10.0991 21.5924 10.2461 21.5174C10.4157 21.4308 10.5356 21.1223 10.7756 20.5054L13.3724 13.8278C13.4194 13.707 13.4429 13.6466 13.4792 13.5957C13.5114 13.5506 13.5508 13.5112 13.5959 13.479C13.6468 13.4427 13.7072 13.4192 13.828 13.3722L20.5056 10.7754Z';

// ── SVG builder ──────────────────────────────────────────────────────────────

function svgToDataUrl(svg: string): string {
  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

function makeArrowSvg(size: number, color: string): string {
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none">`,
    `<path d="${ARROW_PATH}" stroke="#fff" stroke-width="4" stroke-linejoin="round" stroke-linecap="round" fill="none" opacity="0.95"/>`,
    `<path d="${ARROW_PATH}" fill="${color}" stroke="${color}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" opacity="0.92"/>`,
    `</svg>`,
  ].join('');
}

// ── Cursor value builder ────────────────────────────────────────────────────
// -webkit-image-set() for retina on Chrome/Safari, plain url() fallback for Firefox.

function cursorValue(
  cssSize: number,
  color: string,
  hx: number,
  hy: number,
  fallback: string
): string {
  const url1x = svgToDataUrl(makeArrowSvg(cssSize, color));
  const url2x = svgToDataUrl(makeArrowSvg(cssSize * 2, color));

  return [
    `-webkit-image-set(url("${url1x}") 1x, url("${url2x}") 2x) ${hx} ${hy}`,
    `url("${url1x}") ${hx} ${hy}`,
    fallback,
  ].join(', ');
}

// ── Component ───────────────────────────────────────────────────────────────

export default function RetinaCustomCursor() {
  useEffect(() => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const color = '#9B6BDA';
    const arrowSize = 22;

    // Arrow hotspot at tip — (3.35, 3.36) in 24×24 SVG space
    const hx = Math.round((3.35 / 24) * arrowSize);
    const hy = Math.round((3.36 / 24) * arrowSize);

    const arrow = cursorValue(arrowSize, color, hx, hy, 'auto');

    const style = document.createElement('style');
    style.textContent = `
      /* ── 1. Custom arrow everywhere ─────────────────────────────── */
      html,
      html a,
      html button,
      html [role="button"],
      html [role="link"],
      html [role="tab"],
      html [role="menuitem"],
      html [role="menuitemradio"],
      html [role="menuitemcheckbox"],
      html [role="option"],
      html [role="switch"],
      html [role="checkbox"],
      html [role="radio"],
      html [role="slider"],
      html [role="treeitem"],
      html [role="gridcell"],
      html label[for],
      html summary,
      html .cursor-pointer,
      html [style*="cursor: pointer"],
      html [style*="cursor:pointer"] {
        cursor: ${arrow} !important;
      }

      /* ── 2. Text inputs: browser auto (I-beam) ─────────────────── */
      html input,
      html textarea,
      html select,
      html [contenteditable="true"] {
        cursor: auto !important;
      }

      /* ── 3. Disabled: system not-allowed ────────────────────────── */
      html :disabled,
      html [disabled],
      html [aria-disabled="true"],
      html .cursor-not-allowed {
        cursor: not-allowed !important;
      }

      /* ── 4. Drag cursors: preserve system grab/grabbing ─────────── */
      html .cursor-grab,
      html [style*="cursor: grab"],
      html [style*="cursor:grab"] {
        cursor: grab !important;
      }
      html .cursor-grabbing,
      html [style*="cursor: grabbing"],
      html [style*="cursor:grabbing"] {
        cursor: grabbing !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return null;
}
